// US-103 «Отсутствия команды» — read-only экран за KPI-карточкой «Отсутствуют».
// Два блока: «Отсутствуют сейчас» + «Предстоящие отсутствия (30 дней)».
// Дизайн — макет дизайнера (Direction C, design_handoff_team_absences).
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  Alert,
  Box,
  Button,
  Group,
  SimpleGrid,
  Skeleton,
  Text,
  useMantineColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { Calendar, Check, ChevronRight, Plane } from "lucide-react";
import { fetchTeamAbsences, type TeamAbsenceRow, type TeamAbsences } from "../api/client";
import { NEUTRALS, STATUS } from "../theme/tokens";
import { AbsenceRouteModal } from "./AbsenceRouteModal";

const MONO = "var(--mantine-font-family-monospace)";

function daysWord(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return "день";
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return "дня";
  return "дней";
}

function initials(name: string): string {
  return name
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("");
}

function fmtPeriod(start: string, end: string): string {
  const s = dayjs(start);
  const e = dayjs(end);
  return s.isSame(e, "day") ? s.format("DD.MM") : `${s.format("DD.MM")} – ${e.format("DD.MM")}`;
}

type Neutrals = (typeof NEUTRALS)["light"];
type Statuses = (typeof STATUS)["light"];

export function TeamAbsencesView() {
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const scheme = colorScheme === "dark" ? "dark" : "light";
  const neutrals: Neutrals = theme.other?.neutrals?.[scheme] ?? NEUTRALS[scheme];
  const status: Statuses = theme.other?.statuses?.[scheme] ?? STATUS[scheme];

  const [data, setData] = useState<TeamAbsences | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);

  const load = useCallback(() => {
    setError(null);
    fetchTeamAbsences()
      .then(setData)
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (error) {
    return (
      <Box>
        <Header neutrals={neutrals} onHome={() => navigate("/dashboard")} />
        <Alert color="red" title="Не удалось загрузить">
          {error}
          <Group mt="sm">
            <Button size="xs" onClick={load}>
              Повторить
            </Button>
          </Group>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Header neutrals={neutrals} onHome={() => navigate("/dashboard")} />

      {data === null ? (
        <>
          <Section
            icon={Plane}
            title="Отсутствуют сейчас"
            count="·"
            hint=""
            neutrals={neutrals}
            status={status}
          >
            <LoadingBlock neutrals={neutrals} />
          </Section>
          <Box mt={28}>
            <Section
              icon={Calendar}
              title="Предстоящие отсутствия"
              count="·"
              hint=""
              neutrals={neutrals}
              status={status}
            >
              <LoadingBlock neutrals={neutrals} />
            </Section>
          </Box>
        </>
      ) : (
        <>
          <Section
            icon={Plane}
            title="Отсутствуют сейчас"
            count={data.current.length}
            hint={`на ${dayjs(data.today).format("D MMMM")}`}
            neutrals={neutrals}
            status={status}
          >
            {data.current.length === 0 ? (
              <EmptyBlock neutrals={neutrals} status={status} text="Сейчас все на месте 👌" />
            ) : (
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm">
                {data.current.map((r, i) => (
                  <PersonCard
                    key={`${r.employee.id}-${i}`}
                    row={r}
                    kind="now"
                    today={data.today}
                    neutrals={neutrals}
                    status={status}
                    onOpen={setOpenId}
                  />
                ))}
              </SimpleGrid>
            )}
          </Section>

          <Box mt={28}>
            <Section
              icon={Calendar}
              title="Предстоящие отсутствия"
              count={data.upcoming.length}
              hint={`ближайшие ${data.horizon_days} дней`}
              neutrals={neutrals}
              status={status}
            >
              {data.upcoming.length === 0 ? (
                <EmptyBlock
                  neutrals={neutrals}
                  status={status}
                  text={`Ближайшие ${data.horizon_days} дней — без запланированных отсутствий.`}
                />
              ) : (
                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm">
                  {data.upcoming.map((r, i) => (
                    <PersonCard
                      key={`${r.employee.id}-${i}`}
                      row={r}
                      kind="upcoming"
                      today={data.today}
                      neutrals={neutrals}
                      status={status}
                      onOpen={setOpenId}
                    />
                  ))}
                </SimpleGrid>
              )}
            </Section>
          </Box>
        </>
      )}
      <AbsenceRouteModal
        absenceId={openId}
        opened={openId != null}
        onClose={() => setOpenId(null)}
      />
    </Box>
  );
}

function Header({ neutrals, onHome }: { neutrals: Neutrals; onHome: () => void }) {
  return (
    <Box mb={24}>
      <Group gap={8} mb={10}>
        <Text
          fz={12.5}
          c="dimmed"
          style={{ cursor: "pointer" }}
          onClick={onHome}
          role="link"
          tabIndex={0}
        >
          Сводка
        </Text>
        <ChevronRight size={13} color={neutrals.textDim} />
        <Text fz={12.5} fw={500} style={{ color: neutrals.text }}>
          Отсутствия команды
        </Text>
      </Group>
      <Text fz={24} fw={600} style={{ letterSpacing: "-0.02em", color: neutrals.text }}>
        Отсутствия команды
      </Text>
      <Text fz={13} c="dimmed" mt={5}>
        Сейчас отсутствуют и ближайшие 30 дней
      </Text>
    </Box>
  );
}

interface SectionProps {
  icon: typeof Plane;
  title: string;
  count: number | string;
  hint: string;
  neutrals: Neutrals;
  status: Statuses;
  children: React.ReactNode;
}

function Section({ icon: Icon, title, count, hint, neutrals, status, children }: SectionProps) {
  const abs = status.absence;
  return (
    <Box>
      <Group justify="space-between" align="flex-end" mb={12} gap={12}>
        <Group gap={10} align="center">
          <Box
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              flexShrink: 0,
              background: abs.bg,
              color: abs.fg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon size={16} />
          </Box>
          <Text fz={16} fw={600} style={{ letterSpacing: "-0.01em", color: neutrals.text }}>
            {title}
          </Text>
          <Text
            style={{
              fontFamily: MONO,
              fontSize: 12,
              fontWeight: 600,
              color: abs.fg,
              background: abs.bg,
              padding: "2px 8px",
              borderRadius: 999,
            }}
          >
            {count}
          </Text>
        </Group>
        {hint && (
          <Text fz={12} style={{ color: neutrals.textDim }}>
            {hint}
          </Text>
        )}
      </Group>
      {children}
    </Box>
  );
}

interface PersonCardProps {
  row: TeamAbsenceRow;
  kind: "now" | "upcoming";
  today: string;
  neutrals: Neutrals;
  status: Statuses;
  onOpen: (id: number) => void;
}

function PersonCard({ row, kind, today, neutrals, status, onOpen }: PersonCardProps) {
  const abs = status.absence;
  // Заявка ещё не подтверждена — приглушаем карточку в серый и метим, у кого
  // она сейчас. US-107: ступень РП — отдельная метка, иначе линейный
  // руководитель по метке «ждёт ЛР» идёт в свою очередь и не находит заявку.
  const pendingHr = !!row.pending_hr;
  const pendingLr = !!row.pending_lr;
  const pendingPm = !!row.pending_pm;
  const pending = pendingHr || pendingLr || pendingPm;
  const pendingLabel = pendingHr ? "ждёт HR" : pendingPm ? "ждёт РП" : "ждёт ЛР";
  const accFg = pending ? neutrals.textMuted : abs.fg;
  const accBg = pending ? neutrals.surfaceAlt : abs.bg;
  let whenText: string;
  if (kind === "now") {
    const left = (row.days_left ?? 1) - 1; // полных дней после сегодня
    whenText = left <= 0 ? "выходит завтра" : `ещё ${left} ${daysWord(left)}`;
  } else {
    const inDays = dayjs(row.start_date).diff(dayjs(today), "day");
    whenText = inDays <= 1 ? "завтра" : `через ${inDays} ${daysWord(inDays)}`;
  }

  const whenMeta = (
    <Text
      style={{
        fontFamily: MONO,
        fontSize: 12,
        fontWeight: kind === "now" ? 600 : 500,
        color: kind === "now" ? accFg : neutrals.textMuted,
        whiteSpace: "nowrap",
      }}
    >
      {whenText}
    </Text>
  );

  const clickable = row.id != null;
  return (
    <Box
      data-testid="team-absence-card"
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => onOpen(row.id as number) : undefined}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 14,
        padding: "14px 16px",
        background: neutrals.surface,
        border: `1px solid ${neutrals.border}`,
        borderRadius: 12,
        borderLeft: `3px solid ${accFg}`,
        cursor: clickable ? "pointer" : "default",
      }}
    >
      <Box
        style={{
          width: 38,
          height: 38,
          borderRadius: 999,
          flexShrink: 0,
          background: accBg,
          color: accFg,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13.5,
          fontWeight: 600,
        }}
      >
        {initials(row.employee.full_name)}
      </Box>
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Group gap={8} align="baseline" wrap="wrap" style={{ lineHeight: 1.35 }}>
          <Text fz={14.5} fw={600} style={{ color: neutrals.text, whiteSpace: "nowrap" }}>
            {row.employee.full_name}
          </Text>
          <Text fz={12.5} style={{ color: neutrals.textDim }}>
            {row.employee.position || "—"}
          </Text>
        </Group>
        <Group gap={10} align="center" mt={7} wrap="wrap">
          <Text
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "3px 10px",
              borderRadius: 999,
              background: accBg,
              color: accFg,
              fontSize: 11.5,
              fontWeight: 600,
              whiteSpace: "nowrap",
              lineHeight: 1.4,
            }}
          >
            {row.absence_type_display}
          </Text>
          {pending && (
            <Text
              data-testid={pendingHr ? "pending-hr-badge" : "pending-lr-badge"}
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "3px 10px",
                borderRadius: 999,
                background: neutrals.surfaceAlt,
                color: neutrals.textMuted,
                fontSize: 11.5,
                fontWeight: 600,
                whiteSpace: "nowrap",
                lineHeight: 1.4,
              }}
            >
              {pendingLabel}
            </Text>
          )}
          <Text style={{ fontFamily: MONO, fontSize: 12.5, color: neutrals.textMuted }}>
            {fmtPeriod(row.start_date, row.end_date)}
          </Text>
          {/* На узком экране метка времени — под бейджем */}
          <Box hiddenFrom="md">{whenMeta}</Box>
        </Group>
      </Box>
      {/* На десктопе метка времени — справа */}
      <Box visibleFrom="md" style={{ flexShrink: 0, paddingTop: 2 }}>
        {whenMeta}
      </Box>
    </Box>
  );
}

function EmptyBlock({
  neutrals,
  status,
  text,
}: {
  neutrals: Neutrals;
  status: Statuses;
  text: string;
}) {
  return (
    <Box
      style={{
        background: neutrals.surface,
        border: `1px solid ${neutrals.border}`,
        borderRadius: 12,
        padding: "26px 24px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        color: neutrals.textMuted,
        fontSize: 13.5,
      }}
    >
      <Box
        style={{
          width: 34,
          height: 34,
          borderRadius: 999,
          flexShrink: 0,
          background: status.approved.bg,
          color: status.approved.fg,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Check size={18} />
      </Box>
      {text}
    </Box>
  );
}

function LoadingBlock({ neutrals }: { neutrals: Neutrals }) {
  return (
    <Box style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {[0, 1, 2].map((i) => (
        <Group
          key={i}
          gap={14}
          wrap="nowrap"
          style={{
            padding: "14px 16px",
            background: neutrals.surface,
            border: `1px solid ${neutrals.border}`,
            borderRadius: 12,
          }}
        >
          <Skeleton width={38} height={38} radius={999} />
          <Box style={{ flex: 1 }}>
            <Skeleton width={170} height={14} />
            <Box h={9} />
            <Group gap={10}>
              <Skeleton width={120} height={20} radius={999} />
              <Skeleton width={90} height={14} />
            </Group>
          </Box>
          <Skeleton width={70} height={14} />
        </Group>
      ))}
    </Box>
  );
}
